import type { QrScanResult } from './types';

export type ActiveQrScanRequest = {
  readonly generation: number;
  readonly resolve: (result: QrScanResult) => void;
  cameraToken: string | null;
  representativeRobotId: string | null;
  usingLocalCamera: boolean;
};

export type QrScanReservation = {
  request: ActiveQrScanRequest;
  result: Promise<QrScanResult>;
};

export class QrScanCoordinator {
  private generation = 0;
  private active: ActiveQrScanRequest | null = null;

  get current() {
    return this.active;
  }

  reserve(representativeRobotId: string | null): QrScanReservation | null {
    if (this.active) return null;
    let resolve!: (result: QrScanResult) => void;
    const result = new Promise<QrScanResult>((settle) => {
      resolve = settle;
    });
    const request: ActiveQrScanRequest = {
      generation: this.generation + 1,
      resolve,
      cameraToken: null,
      representativeRobotId,
      usingLocalCamera: false,
    };
    this.generation = request.generation;
    this.active = request;
    return { request, result };
  }

  isCurrent(request: ActiveQrScanRequest) {
    return this.active === request && request.generation === this.generation;
  }

  attachCamera(request: ActiveQrScanRequest, cameraToken: string | null) {
    if (!this.isCurrent(request) || request.usingLocalCamera) return false;
    request.cameraToken = cameraToken;
    return true;
  }

  switchToLocalCamera(): string | null {
    const request = this.active;
    if (!request || request.usingLocalCamera) return null;
    request.usingLocalCamera = true;
    const cameraToken = request.cameraToken;
    request.cameraToken = null;
    return cameraToken;
  }

  settle(result: QrScanResult): ActiveQrScanRequest | null {
    const request = this.active;
    if (!request) return null;
    this.active = null;
    this.generation += 1;
    request.resolve(result);
    return request;
  }
}

export const acquireQrCameraLease = async (
  coordinator: QrScanCoordinator,
  request: ActiveQrScanRequest,
  acquireCamera: () => Promise<string | null>,
  releaseCamera: (cameraToken: string) => Promise<void>,
  onAttached?: () => void,
) => {
  const cameraToken = await acquireCamera();
  if (coordinator.attachCamera(request, cameraToken)) {
    onAttached?.();
    return true;
  }
  if (cameraToken) await releaseCamera(cameraToken);
  return false;
};
